import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { clsx } from 'clsx';

interface Props {
  placeholder?: string;
  value:        string;
  onChange:     (value: string) => void;
  className?:   string;
}

/**
 * Styled search input with a 300 ms internal debounce.
 * Maintains its own display state so the UI feels instant while
 * onChange fires only after the user pauses typing.
 */
export default function ListSearchInput({ placeholder = 'Search…', value, onChange, className }: Props) {
  const [local, setLocal] = useState(value);

  // Debounce: propagate to parent 300 ms after the last keystroke
  useEffect(() => {
    const t = setTimeout(() => { if (local !== value) onChange(local); }, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  // Allow parent to reset (e.g. date change clears the search)
  useEffect(() => {
    if (value !== local) setLocal(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function clear() {
    setLocal('');
    onChange('');
  }

  return (
    <div className={clsx('relative', className)}>
      <Search
        size={13}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      />
      <input
        type="search"
        value={local}
        onChange={e => setLocal(e.target.value)}
        placeholder={placeholder}
        className={clsx(
          'w-full py-2 pl-8 border border-gray-200 rounded-xl text-sm bg-white',
          'placeholder:text-gray-400',
          'focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy',
          local ? 'pr-7' : 'pr-3',
        )}
      />
      {local && (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition"
        >
          <X size={12}/>
        </button>
      )}
    </div>
  );
}
